import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaTrophy,
  FaMedal,
  FaHourglassHalf,
  FaFire,
  FaTasks,
  FaStar,
  FaChevronRight,
  FaDatabase,
  FaSquareRootAlt,
  FaAtom,
  FaCode,
} from 'react-icons/fa';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { API_BASE } from '../utils/apiEndpoints';
import { localizeAchievementName } from '../utils/achievements.ts';
import './css/AchievementsPage.css';

interface AchievementItem {
  id: number;
  name: string;
  description: string | null;
  points: number;
  icon?: string | null;
  key?: string | null;
  earned: boolean;
  progressCurrent: number;
  progressTarget: number;
}

interface RecentAchievement {
  id: number;
  name: string;
  points: number;
  icon?: string | null;
  key?: string | null;
  earned_at?: string | null;
}

type CategoryId =
  | 'streak'
  | 'solved'
  | 'special'
  | 'sql'
  | 'math'
  | 'physics'
  | 'algorithms';

type RarityId = 'legendary' | 'epic' | 'rare' | 'common';

const BADGES_PER_ROW = 6;

/** Порядок назначения достижений в категории (сначала темы, «Особые» — остаток). */
const CATEGORY_MATCH_ORDER: CategoryId[] = [
  'streak',
  'solved',
  'sql',
  'math',
  'physics',
  'algorithms',
  'special',
];

/** Порядок отображения рядов на странице. */
const CATEGORY_DISPLAY_ORDER: CategoryId[] = [
  'streak',
  'solved',
  'special',
  'sql',
  'math',
  'physics',
  'algorithms',
];

const CATEGORY_META: Array<{
  id: CategoryId;
  icon: React.ReactNode;
  titleKey: string;
  descKey: string;
  match: (a: AchievementItem) => boolean;
}> = [
  {
    id: 'streak',
    icon: <FaFire />,
    titleKey: 'achievements.catStreak',
    descKey: 'achievements.catStreakDesc',
    match: (a) => (a.key || '').startsWith('streak_'),
  },
  {
    id: 'solved',
    icon: <FaTasks />,
    titleKey: 'achievements.catSolved',
    descKey: 'achievements.catSolvedDesc',
    match: (a) => a.key === 'first_solved',
  },
  {
    id: 'special',
    icon: <FaStar />,
    titleKey: 'achievements.catSpecial',
    descKey: 'achievements.catSpecialDesc',
    match: () => true,
  },
  {
    id: 'sql',
    icon: <FaDatabase />,
    titleKey: 'achievements.catSql',
    descKey: 'achievements.catSqlDesc',
    match: (a) => (a.key || '').startsWith('sql_'),
  },
  {
    id: 'math',
    icon: <FaSquareRootAlt />,
    titleKey: 'achievements.catMath',
    descKey: 'achievements.catMathDesc',
    match: (a) => (a.key || '').startsWith('math_'),
  },
  {
    id: 'physics',
    icon: <FaAtom />,
    titleKey: 'achievements.catPhysics',
    descKey: 'achievements.catPhysicsDesc',
    match: (a) => (a.key || '').startsWith('physics_'),
  },
  {
    id: 'algorithms',
    icon: <FaCode />,
    titleKey: 'achievements.catAlgorithms',
    descKey: 'achievements.catAlgorithmsDesc',
    match: (a) => (a.key || '').startsWith('algorithms_'),
  },
];

function padBadges(items: AchievementItem[]): Array<AchievementItem | null> {
  const sliced = items.slice(0, BADGES_PER_ROW);
  const slots: Array<AchievementItem | null> = [...sliced];
  while (slots.length < BADGES_PER_ROW) slots.push(null);
  return slots;
}

/** Уровень из названия (I–IV) или порог прогресса — для стабильного порядка плашек. */
function achievementSortKey(a: AchievementItem): number {
  if (a.progressTarget > 0) return a.progressTarget;
  const num = (a.key || '').match(/_(\d+)$/);
  return num ? Number(num[1]) : a.id;
}

function sortAchievements(items: AchievementItem[]): AchievementItem[] {
  return [...items].sort((a, b) => {
    const ka = achievementSortKey(a);
    const kb = achievementSortKey(b);
    if (ka !== kb) return ka - kb;
    return a.id - b.id;
  });
}

function progressPercent(current: number, target: number): number {
  if (target <= 0) return 0;
  return Math.min(100, Math.round((current / target) * 100));
}

function iconSrc(a: { id: number; icon?: string | null }): string {
  return a.icon ? `/achievement-icons/${a.icon}` : `/achievement-icons/${a.id}.svg`;
}

/** CSS-модификатор под разный запас полей в растровых ассетах. */
function badgeAssetModifier(icon?: string | null): string {
  if (!icon) return '';
  if (icon.startsWith('achievement_sql_')) return ' ach-badge--sql';
  if (icon.startsWith('achievement_math_')) return ' ach-badge--math';
  if (icon.startsWith('achievement_physics_')) return ' ach-badge--physics';
  if (icon.startsWith('achievement_series_')) return ' ach-badge--series';
  return '';
}

function rarityOf(points: number): RarityId {
  if (points >= 40) return 'legendary';
  if (points >= 25) return 'epic';
  if (points >= 15) return 'rare';
  return 'common';
}

function splitTitle(title: string): { lead: string; accent: string } {
  const parts = title.trim().split(/\s+/);
  if (parts.length <= 1) return { lead: title, accent: '' };
  return { lead: parts.slice(0, -1).join(' '), accent: parts[parts.length - 1] };
}

function formatDate(iso: string | null | undefined, lang: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function CircularProgress({ value, label }: { value: number; label: string }) {
  const size = 52;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, value)) / 100) * c;

  return (
    <div className="ach-ring" aria-hidden>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          className="ach-ring__track"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className="ach-ring__fill"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="ach-ring__value">{label}</span>
    </div>
  );
}

function BadgeIcon({
  achievement,
  size = 'md',
}: {
  achievement: AchievementItem | RecentAchievement;
  size?: 'sm' | 'md' | 'lg';
}) {
  const { t } = useTranslation();
  const earned = 'earned' in achievement ? achievement.earned : true;
  const [failed, setFailed] = useState(false);
  const name = localizeAchievementName(t, achievement);

  const hasAsset = !failed;
  const assetMod = hasAsset ? badgeAssetModifier(achievement.icon) : '';

  return (
    <div
      className={`ach-badge ach-badge--${size}${earned ? ' is-earned' : ' is-locked'}${hasAsset ? ' ach-badge--asset' : ''}${assetMod}`}
      title={name}
    >
      <div className="ach-badge__hex">
        {hasAsset ? (
          <img
            src={iconSrc(achievement)}
            alt=""
            className="ach-badge__img"
            onError={() => setFailed(true)}
          />
        ) : (
          <span className="ach-badge__fallback" aria-hidden>
            <FaTrophy />
          </span>
        )}
      </div>
    </div>
  );
}

function AchievementsPage() {
  const { userId } = useParams<{ userId: string }>();
  const { t, currentLanguage } = useTranslation();
  const [list, setList] = useState<AchievementItem[]>([]);
  const [recent, setRecent] = useState<RecentAchievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileUsername, setProfileUsername] = useState<string | null>(null);

  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        setLoading(true);
        setError(null);
        setProfileUsername(null);
        setRecent([]);

        const url = userId
          ? `${API_BASE}/api/achievements/user/${userId}`
          : `${API_BASE}/api/achievements`;
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const achievements: AchievementItem[] = data.achievements ?? [];
        setList(achievements);

        if (userId) {
          const profileRes = await fetch(`${API_BASE}/api/profile/user/${userId}`, {
            credentials: 'include',
          });
          if (profileRes.ok) {
            const profileData = await profileRes.json();
            setProfileUsername(profileData.username ?? null);
            const earned = (profileData.achievements ?? []) as RecentAchievement[];
            setRecent(
              [...earned]
                .sort((a, b) => {
                  const ta = a.earned_at ? new Date(a.earned_at).getTime() : 0;
                  const tb = b.earned_at ? new Date(b.earned_at).getTime() : 0;
                  return tb - ta;
                })
                .slice(0, 5),
            );
          }
        } else {
          const meRes = await fetch(`${API_BASE}/api/achievements/me`, {
            credentials: 'include',
          });
          if (meRes.ok) {
            const meData = await meRes.json();
            const earned = (meData.achievements ?? []) as RecentAchievement[];
            setRecent(
              [...earned]
                .sort((a, b) => {
                  const ta = a.earned_at ? new Date(a.earned_at).getTime() : 0;
                  const tb = b.earned_at ? new Date(b.earned_at).getTime() : 0;
                  return tb - ta;
                })
                .slice(0, 5),
            );
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t('achievements.loadError'));
        setList([]);
      } finally {
        setLoading(false);
      }
    };
    void fetchAchievements();
  }, [t, userId]);

  const stats = useMemo(() => {
    const total = list.length;
    const earned = list.filter((a) => a.earned).length;
    const inProgress = list.filter(
      (a) => !a.earned && a.progressCurrent > 0 && a.progressTarget > 0,
    ).length;
    const overall = total > 0 ? Math.round((earned / total) * 100) : 0;
    return { total, earned, inProgress, overall };
  }, [list]);

  const categories = useMemo(() => {
    const byId = new Map(CATEGORY_META.map((m) => [m.id, m]));
    const assigned = new Set<number>();
    const buckets = new Map<CategoryId, AchievementItem[]>();
    for (const id of CATEGORY_MATCH_ORDER) buckets.set(id, []);

    for (const catId of CATEGORY_MATCH_ORDER) {
      const meta = byId.get(catId);
      if (!meta) continue;
      const bucket = buckets.get(catId)!;
      for (const a of list) {
        if (assigned.has(a.id)) continue;
        if (!meta.match(a)) continue;
        assigned.add(a.id);
        bucket.push(a);
      }
    }

    return CATEGORY_DISPLAY_ORDER.map((id) => {
      const meta = byId.get(id)!;
      const items = sortAchievements(buckets.get(id) ?? []);
      return {
        ...meta,
        items,
        slots: padBadges(items),
        earnedCount: items.filter((a) => a.earned).length,
        totalCount: BADGES_PER_ROW,
      };
    });
  }, [list]);

  const nearestGoal = useMemo(() => {
    const candidates = list
      .filter((a) => !a.earned && a.progressTarget > 0)
      .map((a) => ({
        ...a,
        pct: progressPercent(a.progressCurrent, a.progressTarget),
        remaining: Math.max(0, a.progressTarget - a.progressCurrent),
      }))
      .sort((a, b) => {
        if (b.pct !== a.pct) return b.pct - a.pct;
        return a.remaining - b.remaining;
      });
    return candidates[0] ?? null;
  }, [list]);

  const rarityStats = useMemo(() => {
    const tiers: Array<{ id: RarityId; labelKey: string }> = [
      { id: 'legendary', labelKey: 'achievements.rarityLegendary' },
      { id: 'epic', labelKey: 'achievements.rarityEpic' },
      { id: 'rare', labelKey: 'achievements.rarityRare' },
      { id: 'common', labelKey: 'achievements.rarityCommon' },
    ];
    return tiers.map((tier) => {
      const items = list.filter((a) => rarityOf(a.points) === tier.id);
      const earned = items.filter((a) => a.earned).length;
      return {
        ...tier,
        earned,
        total: items.length,
        pct: items.length > 0 ? Math.round((earned / items.length) * 100) : 0,
      };
    }).filter((t) => t.total > 0);
  }, [list]);

  const titleRaw = profileUsername
    ? `${t('achievements.title')} — ${profileUsername}`
    : t('achievements.title');
  const { lead, accent } = splitTitle(profileUsername ? t('achievements.title') : titleRaw);

  if (loading) {
    return (
      <div className="ach-page">
        <div className="ach-inner">
          <div className="ach-state">{t('achievements.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ach-page">
        <div className="ach-inner">
          <div className="ach-state ach-state--error">
            {t('achievements.errorPrefix')} {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ach-page">
      <div className="ach-inner">
        <header className="ach-hero">
          <h1 className="ach-hero__title">
            <span className="ach-hero__lead">{lead}</span>
            {accent ? <span className="ach-hero__accent"> {accent}</span> : null}
            {profileUsername ? (
              <span className="ach-hero__user"> — {profileUsername}</span>
            ) : null}
          </h1>
          <p className="ach-hero__subtitle">{t('achievements.subtitle')}</p>
        </header>

        <section className="ach-stats" aria-label={t('achievements.statsAria')}>
          <div className="ach-stat-card">
            <span className="ach-stat-card__icon" aria-hidden>
              <FaTrophy />
            </span>
            <div className="ach-stat-card__body">
              <span className="ach-stat-card__title">{t('achievements.statTotalTitle')}</span>
              <span className="ach-stat-card__value">{stats.total}</span>
              <span className="ach-stat-card__label">{t('achievements.statAvailable')}</span>
            </div>
          </div>
          <div className="ach-stat-card">
            <span className="ach-stat-card__icon" aria-hidden>
              <FaMedal />
            </span>
            <div className="ach-stat-card__body">
              <span className="ach-stat-card__title">{t('achievements.statEarnedTitle')}</span>
              <span className="ach-stat-card__value">{stats.earned}</span>
              <span className="ach-stat-card__label">{t('achievements.statAchievements')}</span>
            </div>
          </div>
          <div className="ach-stat-card">
            <span className="ach-stat-card__icon ach-stat-card__icon--gold" aria-hidden>
              <FaHourglassHalf />
            </span>
            <div className="ach-stat-card__body">
              <span className="ach-stat-card__title">{t('achievements.statInProgressTitle')}</span>
              <span className="ach-stat-card__value">{stats.inProgress}</span>
              <span className="ach-stat-card__label">{t('achievements.statAchievements')}</span>
            </div>
          </div>
          <div className="ach-stat-card ach-stat-card--progress">
            <CircularProgress value={stats.overall} label={`${stats.overall}%`} />
            <div className="ach-stat-card__body">
              <span className="ach-stat-card__title">{t('achievements.statOverall')}</span>
              <span className="ach-stat-card__meta">
                {stats.earned} / {stats.total}
              </span>
            </div>
          </div>

          <section className="ach-goal-card">
            {nearestGoal ? (
              <>
                <div className="ach-goal-card__top">
                  <BadgeIcon achievement={nearestGoal} size="md" />
                  <div className="ach-goal-card__head">
                    <h2 className="ach-goal-card__title">{t('achievements.nearestGoal')}</h2>
                    <span className="ach-goal-card__name">{localizeAchievementName(t, nearestGoal)}</span>
                  </div>
                </div>
                <div className="ach-goal-card__footer">
                  <span className="ach-goal-card__progress">
                    {nearestGoal.progressCurrent} / {nearestGoal.progressTarget}
                  </span>
                  <span className="ach-goal-card__xp">+{nearestGoal.points} XP</span>
                </div>
                <div className="ach-goal-card__bar">
                  <div
                    className="ach-goal-card__fill"
                    style={{ width: `${nearestGoal.pct}%` }}
                  />
                </div>
              </>
            ) : (
              <>
                <h2 className="ach-goal-card__title">{t('achievements.nearestGoal')}</h2>
                <p className="ach-goal-card__empty">{t('achievements.nearestGoalEmpty')}</p>
              </>
            )}
          </section>
        </section>

        <div className="ach-layout">
          <div className="ach-main">
            {categories.map((cat) => (
              <section key={cat.id} className="ach-row">
                <div className="ach-row__meta">
                  <span className="ach-row__icon" aria-hidden>
                    {cat.icon}
                  </span>
                  <div className="ach-row__text">
                    <h2 className="ach-row__title">{t(cat.titleKey)}</h2>
                    <p className="ach-row__desc">{t(cat.descKey)}</p>
                  </div>
                </div>
                <div className="ach-row__badges">
                  {cat.slots.map((a, index) =>
                    a ? (
                      <BadgeIcon key={a.id} achievement={a} />
                    ) : (
                      <div
                        key={`empty-${cat.id}-${index}`}
                        className="ach-badge ach-badge--md is-locked"
                        aria-hidden
                      >
                        <div className="ach-badge__hex">
                          <span className="ach-badge__fallback">
                            <FaTrophy />
                          </span>
                        </div>
                      </div>
                    ),
                  )}
                </div>
                <div className="ach-row__progress">
                  <div className="ach-row__progress-text">
                    <span className="ach-row__progress-count">
                      {cat.earnedCount} / {cat.totalCount}
                    </span>
                    <span className="ach-row__progress-label">
                      {t('achievements.rowProgressLabel')}
                    </span>
                  </div>
                  <FaChevronRight className="ach-row__progress-chevron" aria-hidden />
                </div>
              </section>
            ))}
          </div>

          <aside className="ach-side">
            {rarityStats.length > 0 ? (
              <section className="ach-side-card">
                <h2 className="ach-side-card__title">{t('achievements.rarityTitle')}</h2>
                <ul className="ach-rarity">
                  {rarityStats.map((tier) => (
                    <li key={tier.id} className={`ach-rarity__item ach-rarity__item--${tier.id}`}>
                      <div className="ach-rarity__head">
                        <span className="ach-rarity__label">{t(tier.labelKey)}</span>
                        <span className="ach-rarity__count">
                          {tier.earned} / {tier.total}
                        </span>
                      </div>
                      <div className="ach-rarity__bar">
                        <div
                          className="ach-rarity__fill"
                          style={{ width: `${tier.pct}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section className="ach-side-card">
              <h2 className="ach-side-card__title">{t('achievements.recentTitle')}</h2>
              {recent.length === 0 ? (
                <p className="ach-side-card__empty">{t('achievements.recentEmpty')}</p>
              ) : (
                <ul className="ach-recent">
                  {recent.map((a) => (
                    <li key={a.id} className="ach-recent__item">
                      <BadgeIcon achievement={{ ...a, earned: true }} size="sm" />
                      <div className="ach-recent__text">
                        <span className="ach-recent__name">{localizeAchievementName(t, a)}</span>
                        <span className="ach-recent__meta">
                          <span className="ach-recent__xp">+{a.points} XP</span>
                          {a.earned_at ? (
                            <span className="ach-recent__date">
                              {t('achievements.receivedOn')}{' '}
                              {formatDate(a.earned_at, currentLanguage)}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default AchievementsPage;
